/**
 * Job progress panel (MUI port of the §3 tracker, P2-15 / P4-14).
 *
 * Uses the shared useJobPolling hook (terminal stop, backoff, visibility
 * pause, abort on unmount). Shows each step with its state, rule version
 * and row counts. Row counts of zero are not rendered — an output workbook
 * whose deliverable is the Summary is not "0 rows" (dashboard defect).
 */
import { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { JobState, StepState } from "../lib/api";
import { useJobPolling } from "../hooks/useJobPolling";
import { StateChip } from "./ui/StateChip";
import { ErrorAlert } from "./ui/ErrorAlert";
import { SectionLabel } from "./ui/SectionLabel";
import { ERROR_DETAIL_MAX_CHARS } from "../lib/config";
import { ArtifactFiles } from "./ArtifactFiles";

export function JobProgress({
  jobId,
  onState,
  showFiles = true,
}: {
  jobId: string;
  onState?: (job: JobState) => void;
  showFiles?: boolean;
}) {
  const { t } = useTranslation();
  const { job, error, isLoading, refetch, terminal } = useJobPolling(jobId);

  useEffect(() => {
    if (job) onState?.(job);
  }, [job, onState]);

  if (isLoading) return <LinearProgress />;
  if (!job) {
    return <ErrorAlert error={error} onRetry={() => void refetch()} />;
  }

  return (
    <Card>
      {!terminal && <LinearProgress />}
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <SectionLabel>{job.dag_name}</SectionLabel>
            <Link
              component={RouterLink}
              to={`/runs/${job.job_id}`}
              underline="hover"
              sx={{
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              {t("runDetail.viewRun")} <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Link>
          </Stack>
          <StateChip state={job.state} fallbackLabel={job.dashboard_status} />
        </Stack>

        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {job.steps.map((step) => (
            <StepRow key={step.step_key} step={step} />
          ))}
        </Stack>

        {showFiles && job.artifacts.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <SectionLabel>{t("files.produced")}</SectionLabel>
            <ArtifactFiles artifacts={job.artifacts} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function StepRow({ step }: { step: StepState }) {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      sx={{ borderBottom: 1, borderColor: "divider", pb: 1, "&:last-child": { border: 0 } }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {step.step_key}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {step.tool_name}
          {step.rule_version ? ` · ${step.rule_version}` : ""}
        </Typography>
        {step.error_detail && (
          <Typography
            variant="caption"
            className="num"
            sx={{ color: "error.main", whiteSpace: "pre-wrap", display: "block", mt: 0.5 }}
          >
            {step.error_detail.slice(0, ERROR_DETAIL_MAX_CHARS)}
          </Typography>
        )}
      </Box>
      <Stack alignItems="flex-end" spacing={0.5} flexShrink={0}>
        <StateChip state={step.state} />
        {step.rows_out > 0 && (
          <Typography variant="caption" className="num" color="text.secondary">
            {t("files.rows", { count: step.rows_out })}
          </Typography>
        )}
        {step.unmatched_rows > 0 && (
          <Typography variant="caption" className="num" sx={{ color: "warning.main" }}>
            {t("files.unmatchedShort", { count: step.unmatched_rows })}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
